/**
 * The hosted cockpit's sign-in page.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C_AUTH. `CLOUD_UX_SPEC.md` §
 * "First-run" makes this step 1: "Sign in (`C_AUTH` Login page) → land on
 * Overview showing an empty-but-honest state". Honest is the operative word
 * here too — this page never claims to know more about a failed sign-in than
 * the server told it.
 *
 * WHAT THIS COMPONENT DOES NOT DO
 *   - It does not read, write, or hold a session token. The credential is an
 *     `HttpOnly` cookie the browser manages; `api/auth.ts` explains why there
 *     is no `localStorage` anywhere in this surface (rule I-7).
 *   - It does not interpret a 401. The server returns one message for an
 *     unknown address and for a wrong password, and this page renders that
 *     message verbatim. A friendlier "no account with that email" would be an
 *     account-existence oracle rebuilt in the browser.
 *   - It does not decide what the user may do next. Authorization is the API's
 *     (`CLOUD_SECURITY_AND_TENANCY_V1` §3); a signed-in session is not a
 *     permission, and no capability is inferred here.
 *
 * BUILD STATUS: compiled and exercised as part of the Vite customer SPA.
 */

import React, { useCallback, useState } from 'react';
import { Link, useSearch } from 'wouter';

import { AuthApiError, AuthSession, login, requestPasswordReset } from '../../api/auth';
import { PasswordField } from '../../components/PasswordField';

export interface LoginPageProps {
  /** Called once a session exists. Navigation is the host app's business. */
  onSignedIn?: (session: AuthSession) => void;
  /** Pre-filled address, e.g. from an invitation link. Never a password. */
  initialEmail?: string;
}

type Mode = 'sign-in' | 'reset';

export function LoginPage({ onSignedIn, initialEmail = '' }: LoginPageProps): React.ReactElement {
  const [mode, setMode] = useState<Mode>('sign-in');
  const search = useSearch();
  // A finished account-setup page links here with the address the person just typed (never a password).
  const [email, setEmail] = useState(() => initialEmail || new URLSearchParams(search).get('email') || '');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const handleSignIn = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (busy) {
        return;
      }
      setBusy(true);
      setError('');
      setNotice('');
      try {
        const session = await login({ email, password });
        // The password is dropped from component state the moment it has been
        // exchanged for a session. It is never persisted, and there is nothing
        // to persist it to.
        setPassword('');
        if (onSignedIn) {
          onSignedIn(session);
        }
      } catch (caught) {
        if (caught instanceof AuthApiError) {
          // Rendered verbatim, including the rate-limit message. This branch
          // deliberately does not distinguish 401 causes — see the header.
          setError(
            caught.isRateLimited && caught.retryAfterSeconds
              ? `${caught.message} (try again in ${caught.retryAfterSeconds}s)`
              : caught.message,
          );
        } else {
          setError('sign-in could not be completed');
        }
      } finally {
        setBusy(false);
      }
    },
    [busy, email, password, onSignedIn],
  );

  const handleReset = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (busy) {
        return;
      }
      setBusy(true);
      setError('');
      setNotice('');
      try {
        // The acknowledgement is identical for an address with an account and
        // one without, and it is shown exactly as the server phrased it.
        setNotice(await requestPasswordReset(email));
      } catch (caught) {
        setError(
          caught instanceof AuthApiError ? caught.message : 'the request could not be completed',
        );
      } finally {
        setBusy(false);
      }
    },
    [busy, email],
  );

  return (
    <main className="auteur-login" aria-labelledby="auteur-login-heading">
      <h1 id="auteur-login-heading">
        {mode === 'sign-in' ? 'Sign in to Auteur Cloud' : 'Reset your password'}
      </h1>

      <form onSubmit={mode === 'sign-in' ? handleSignIn : handleReset} noValidate>
        <label htmlFor="auteur-login-email">Email</label>
        <input
          id="auteur-login-email"
          name="email"
          type="email"
          autoComplete="username"
          required
          value={email}
          disabled={busy}
          onChange={(event) => setEmail(event.target.value)}
        />

        {mode === 'sign-in' && (
          <PasswordField
            id="auteur-login-password"
            name="password"
            label="Password"
            autoComplete="current-password"
            value={password}
            disabled={busy}
            onChange={setPassword}
          />
        )}

        {error && (
          <p className="auteur-login-error" role="alert">
            {error}
          </p>
        )}
        {notice && (
          <p className="auteur-login-notice" role="status">
            {notice}
          </p>
        )}

        <button type="submit" disabled={busy || !email || (mode === 'sign-in' && !password)}>
          {busy ? 'Working…' : mode === 'sign-in' ? 'Sign in' : 'Send reset link'}
        </button>
      </form>

      <button
        type="button"
        className="auteur-login-switch"
        disabled={busy}
        onClick={() => {
          setMode(mode === 'sign-in' ? 'reset' : 'sign-in');
          setPassword('');
          setError('');
          setNotice('');
        }}
      >
        {mode === 'sign-in' ? 'Forgot your password?' : 'Back to sign in'}
      </button>
      <p>Waiting to verify your email? <Link to="/verify-email">Resend verification</Link>.</p>
    </main>
  );
}

export default LoginPage;
